import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { BellIcon, BookOpenIcon, FlameIcon, HomeIcon, LanguagesIcon, LibraryIcon, SparklesIcon, UserIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import { Logo } from "../ui/Logo";
import { ThemeToggle } from "../ui/ThemeToggle";
import { CourseSwitcher } from "./CourseSwitcher";
import { NotificationBell } from "../notifications/NotificationBell";
import { spring } from "../../utils/motion";
import { learnPath, translatePath, wordsPath } from "../../utils/routes";
interface NavItem {
  to: string;
  label: string;
  icon: typeof HomeIcon;
  match: (path: string) => boolean;
  badge?: number;
  railOnly?: boolean;
}
export function AppLayout() {
  const {
    pathname
  } = useLocation();
  const {
    activeCourse,
    streak,
    plan,
    user
  } = useLearner();
  const {
    dueWords
  } = useWords();
  const {
    unreadCount
  } = useNotifications();
  const due = dueWords(activeCourse?.id).length;
  const nav: NavItem[] = [{
    to: "/app",
    label: "Home",
    icon: HomeIcon,
    match: p => p === "/app" || p === "/app/"
  }, {
    to: activeCourse ? learnPath(activeCourse) : "/app/courses",
    label: "Learn",
    icon: BookOpenIcon,
    match: p => p.startsWith("/app/learn") || p.startsWith("/app/courses")
  }, {
    to: activeCourse ? translatePath(activeCourse.base, activeCourse.target) : "/app/translate",
    label: "Translate",
    icon: LanguagesIcon,
    match: p => p.startsWith("/app/translate")
  }, {
    to: wordsPath(activeCourse),
    label: "Words",
    icon: LibraryIcon,
    match: p => p.startsWith("/app/words"),
    badge: due
  }, {
    to: "/app/notifications",
    label: "Notifications",
    icon: BellIcon,
    match: p => p.startsWith("/app/notifications"),
    badge: unreadCount,
    railOnly: true
  }, {
    to: "/app/profile",
    label: "Profile",
    icon: UserIcon,
    match: p => p.startsWith("/app/profile")
  }];
  return <div className="pat-grid min-h-screen w-full bg-paper font-sans text-ink" data-id="element-435" data-mp="2f4ahwydt7kxn">
      {/* Desktop rail */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r-2 border-line/70 bg-surface px-4 py-5 lg:flex" data-id="element-436" data-mp="2a94pdgpwj80l">
        <div className="px-1" data-id="element-437" data-mp="21bc8w98g1ubm">
          <Logo to="/app" data-id="element-438" data-mp="21cdv99uwu38n" />
        </div>
        <div className="mt-6" data-id="element-439" data-mp="1wbpsxs18dzz4">
          <CourseSwitcher data-id="element-440" data-mp="1wbgcow5zn76m" />
        </div>
        <nav aria-label="App" className="mt-6 flex flex-col gap-1" data-id="element-441" data-mp="1waeqpwnfp0a4">
          {nav.map(item => {
          const active = item.match(pathname);
          const Icon = item.icon;
          return <Link key={item.label} to={item.to} aria-current={active ? "page" : undefined} className={`focus-ring relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold transition-colors duration-150 ${active ? "text-ink" : "text-ink-2 hover:bg-sunken/70 hover:text-ink"}`} data-id="element-442" data-mp="1mfmq0jyk1xb4">
                {active && <motion.span layoutId="rail-active" transition={spring.snappy} className="absolute inset-0 rounded-xl border-2 border-line/60 bg-brand-tint" aria-hidden="true" data-id="element-443" data-mp="1wj03qe3phra" />}
                <Icon className="relative h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-444" data-mp="3aajaqs5n8d1e" />
                <span className="relative flex-1" data-id="element-445" data-mp="3f9uri160fiiz">{item.label}</span>
                {item.badge ? <span className={`relative rounded-md px-1.5 text-xs font-bold ${item.label === "Notifications" ? "bg-bad text-white" : "bg-accent text-night"}`} data-id="element-446" data-mp="3ka0f9695skvs">
                    {item.badge}
                  </span> : null}
              </Link>;
        })}
        </nav>

        <div className="mt-auto space-y-3" data-id="element-447" data-mp="23hdekcw6qbdq">
          {plan === "free" && <Link to="/app/profile/subscription" className="focus-ring tex-ink-dots block rounded-card border-2 border-line/60 bg-peach p-4 text-ink transition-colors duration-150 hover:bg-accent/70" data-id="element-448" data-mp="23jg8h80gkonq">
              <span className="flex items-center gap-2 text-sm font-bold" data-id="element-449" data-mp="2re0we61nnppy">
                <SparklesIcon className="h-4 w-4" data-id="element-450" data-mp="2mghix8cs7gz2" /> Try Plus free
              </span>
              <span className="mt-1 block text-xs text-ink-2" data-id="element-451" data-mp="307csynz0o448">Unlimited hearts, offline book & all games.</span>
            </Link>}
          <div className="flex items-center gap-3 rounded-xl px-1" data-id="element-452" data-mp="2w6u492sf7e69">
            <Link to="/app/profile" className="focus-ring flex min-w-0 flex-1 items-center gap-3 rounded-xl" data-id="element-453" data-mp="3a5ag18553uv9">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border-2 border-line/60 bg-accent text-sm font-bold text-night" data-id="element-454" data-mp="3578nlwltqj74">
                {user.initials}
              </span>
              <span className="min-w-0" data-id="element-455" data-mp="3trlvhr6lu2e5">
                <span className="block truncate text-sm font-semibold" data-id="element-456" data-mp="3tqkel62wqloi">{user.name}</span>
                <span className="flex items-center gap-1 text-xs text-ink-2" data-id="element-457" data-mp="3etjr5err1cvu">
                  <FlameIcon className="h-3.5 w-3.5" data-id="element-458" data-mp="3js4mazs596df" /> {streak}-day streak
                </span>
              </span>
            </Link>
            <ThemeToggle data-id="element-459" data-mp="35zoj9yz8t8ci" />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 border-b-2 border-line/60 bg-paper/90 backdrop-blur-md lg:hidden" data-id="element-460" data-mp="3auklfry71c3c">
        <div className="flex h-16 items-center justify-between gap-3 px-4" data-id="element-461" data-mp="2w1qvtdp7jfky">
          <div className="flex min-w-0 items-center gap-2.5" data-id="element-462" data-mp="312y3zdv9igs5">
            <Logo to="/app" compact data-id="element-463" data-mp="2n2nb65ae6s4c" />
            <CourseSwitcher compact data-id="element-464" data-mp="2n3owr3p1ax0b" />
          </div>
          <div className="flex items-center gap-2" data-id="element-465" data-mp="2r4qo6ycpm4rq">
            <span className="flex items-center gap-1 rounded-xl bg-accent px-2.5 py-1.5 text-sm font-bold text-night" aria-label={`${streak}-day streak`} data-id="element-466" data-mp="2r6l94cnzdwvt">
              <FlameIcon className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" data-id="element-467" data-mp="ssylhkyj3200" />
              {streak}
            </span>
            <NotificationBell align="right" data-id="element-468" data-mp="iwvikcvuqzc0" />
            <ThemeToggle data-id="element-469" data-mp="iy6n4ex4gb46" />
          </div>
        </div>
      </header>

      <main className="pb-28 lg:pb-12 lg:pl-64" data-id="element-470" data-mp="1gl5ov42o898x">
        <Outlet data-id="element-471" data-mp="1gjukb21eixgr" />
      </main>

      {/* Mobile tabs */}
      <nav aria-label="App" className="fixed inset-x-0 bottom-0 z-30 border-t-2 border-line/60 bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden" data-id="element-472" data-mp="1bl9m185pmnuy">
        <ul className="grid grid-cols-5" data-id="element-473" data-mp="12l4orqu8d5ui">
          {nav.filter(item => !item.railOnly).map(item => {
          const active = item.match(pathname);
          const Icon = item.icon;
          return <li key={item.label} data-id="element-474" data-mp="3n9io56s4lvg9">
                  <Link to={item.to} aria-current={active ? "page" : undefined} className="focus-ring flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold" data-id="element-475" data-mp="22t1ed18mp3q9">
                    <span className="relative grid h-8 w-12 place-items-center" data-id="element-476" data-mp="2bsxwsl4fd6cv">
                      {active && <motion.span layoutId="tab-active" transition={spring.snappy} className="absolute inset-0 rounded-xl bg-brand-tint" aria-hidden="true" data-id="element-477" data-mp="2glr1j8zw8eoy" />}
                      <Icon className={`relative h-5 w-5 ${active ? "text-ink" : "text-ink-2"}`} strokeWidth={2} aria-hidden="true" data-id="element-478" data-mp="2ui4fw994rzz7" />
                      {item.badge ? <span className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-brand px-0.5 text-[9px] font-bold text-white" data-id="element-479" data-mp="107r1zz4ymr28">
                          {item.badge}
                        </span> : null}
                    </span>
                    <span className={active ? "text-ink" : "text-ink-2"} data-id="element-480" data-mp="330g4z78zc9ae">{item.label}</span>
                  </Link>
                </li>;
        })}
        </ul>
      </nav>
    </div>;
}